import React, { createContext, useContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNotificacao } from './NotificacaoContext';
import * as pagamentoService from '../services/pagamentoService';

const PagamentosContext = createContext({});

export const PagamentosProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [statusVerificacao, setStatusVerificacao] = useState(null);
  const [verificacaoAtiva, setVerificacaoAtiva] = useState(false);
  const [error, setError] = useState(null);
  const [emailNotifications, setEmailNotifications] = useState({});
  const { adicionarNotificacao } = useNotificacao();

  // Função para envio de emails
  const sendPaymentEmail = useCallback(async (type, prestacaoId) => {
    const types = {
      fatura: 'faturaCriada',
      lembrete: 'lembretePagamento',
      vencido: 'avisoPagamentoVencido',
      restricao: 'avisoRestricaoAcesso'
    };
    
    try {
      console.log(`[PagamentosContext] Enviando email ${types[type]} para prestação ${prestacaoId}`);
      await pagamentoService.sendPaymentEmail(prestacaoId, types[type]);
      
      setEmailNotifications(prev => ({
        ...prev,
        [prestacaoId]: { 
          type, 
          sent: true, 
          timestamp: new Date().toISOString() 
        }
      }));

      adicionarNotificacao('Email enviado com sucesso', 'success');
      return true;
    } catch (error) {
      console.error('[PagamentosContext] Erro ao enviar email:', error);
      setError(error.message);
      adicionarNotificacao('Erro ao enviar email', 'error');
      return false;
    }
  }, [adicionarNotificacao]);

  // Função para gerar fatura com envio de email
  const gerarFaturaProforma = useCallback(async (prestacaoId) => {
    try {
      setLoading(true);
      console.log(`[PagamentosContext] Gerando fatura proforma para prestação ${prestacaoId}`);
      
      const response = await pagamentoService.gerarFaturaProforma(prestacaoId);
      
      setEmailNotifications(prev => ({
        ...prev,
        [prestacaoId]: { 
          type: 'fatura', 
          sent: true, 
          timestamp: new Date().toISOString() 
        }
      }));

      adicionarNotificacao('Fatura gerada e enviada por email', 'success');
      return response;
    } catch (error) {
      console.error('[PagamentosContext] Erro ao gerar fatura:', error);
      setError(error.message);
      adicionarNotificacao('Erro ao gerar fatura', 'error');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [adicionarNotificacao]);

  // Função para verificar status com logs melhorados
  const verificarStatus = useCallback(async (pagamentoId) => {
    try {
      if (!pagamentoId) {
        console.error('[PagamentosContext] ID do pagamento não fornecido');
        throw new Error('ID do pagamento não fornecido');
      }

      console.log(`[PagamentosContext] Iniciando verificação de status para pagamento ${pagamentoId}`);
      const response = await pagamentoService.verificarStatusPagamento(pagamentoId);

      if (!response || !response.status) {
        throw new Error('Resposta inválida ao verificar status');
      }

      setStatusVerificacao(response.status);
      setError(null);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      console.error('[PagamentosContext] Erro ao verificar status:', errorMessage);
      setError(errorMessage);
      throw error;
    }
  }, []);
  // Função para iniciar verificação periódica
  const iniciarVerificacaoPeriodica = useCallback(async (pagamentoId, callbacks = {}) => {
    const { onSuccess, onError, onTimeout, onStatusChange } = callbacks;

    if (!pagamentoId) {
      const error = new Error('ID do pagamento não fornecido para verificação periódica');
      console.error('[PagamentosContext]', error);
      onError?.(error);
      return;
    }

    let tentativas = 0;
    const MAX_TENTATIVAS = 6;
    const INTERVALO = 5000;
    setVerificacaoAtiva(true);
    setError(null);

    console.log(`[PagamentosContext] Iniciando verificação periódica para pagamento ${pagamentoId}`);

    const verificador = setInterval(async () => {
      try {
        if (tentativas >= MAX_TENTATIVAS) {
          clearInterval(verificador);
          setVerificacaoAtiva(false);
          onTimeout?.();
          adicionarNotificacao(
            'O pagamento está em processamento. Você será notificado quando for confirmado.',
            'info'
          );
          return;
        }

        const response = await verificarStatus(pagamentoId);
        onStatusChange?.(response.status);

        if (response.status === 'CONFIRMADO') {
          clearInterval(verificador);
          setVerificacaoAtiva(false);
          adicionarNotificacao('Pagamento confirmado com sucesso!', 'success');
          onSuccess?.(response);
          return;
        }
        
        if (response.status === 'REJEITADO') {
          clearInterval(verificador);
          setVerificacaoAtiva(false);
          const error = new Error(response.mensagem || 'Pagamento rejeitado');
          setError(error.message);
          adicionarNotificacao('Pagamento rejeitado', 'error');
          onError?.(error);
          return;
        }
        
        tentativas++;
      } catch (error) {
        clearInterval(verificador);
        setVerificacaoAtiva(false);
        setError(error.message);
        onError?.(error);
      }
    }, INTERVALO);

    return () => {
      clearInterval(verificador);
      setVerificacaoAtiva(false);
    };
  }, [verificarStatus, adicionarNotificacao]);

  // Função para realizar pagamento
  const realizarPagamento = useCallback(async (dadosPagamento, callbacks = {}) => {
    try {
      setLoading(true);
      setError(null);
      console.log('[PagamentosContext] Iniciando novo pagamento:', dadosPagamento);

      if (!dadosPagamento.prestacaoId) {
        throw new Error('ID da prestação não fornecido');
      }

      const resultado = await pagamentoService.realizarPagamento(dadosPagamento);
      
      if (!resultado.id) {
        throw new Error('ID do pagamento não retornado');
      }

      console.log('[PagamentosContext] Pagamento registrado com ID:', resultado.id);

      adicionarNotificacao(
        'Pagamento registrado, aguardando confirmação...',
        'info'
      );

      const cancelarVerificacao = await iniciarVerificacaoPeriodica(
        resultado.id,
        callbacks
      );

      return {
        ...resultado,
        cancelarVerificacao
      };

    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [iniciarVerificacaoPeriodica, adicionarNotificacao]);

  // Função para verificar status de email
  const checkEmailStatus = useCallback((prestacaoId) => {
    return emailNotifications[prestacaoId] || null;
  }, [emailNotifications]);

  const contextValue = {
    loading,
    statusVerificacao,
    verificacaoAtiva,
    error,
    emailNotifications,
    realizarPagamento,
    verificarStatus,
    iniciarVerificacaoPeriodica,
    sendPaymentEmail,
    gerarFaturaProforma,
    checkEmailStatus
  };

  return (
    <PagamentosContext.Provider value={contextValue}>
      {children}
    </PagamentosContext.Provider>
  );
};

PagamentosProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export const usePagamentos = () => {
  const context = useContext(PagamentosContext);
  if (!context) {
    throw new Error('usePagamentos deve ser usado dentro de um PagamentosProvider');
  }
  return context;
};

export default PagamentosContext;